<template>
  <div
    :class="[
      'banner-item',
      { 'banner-item_small': bannerSmall },
      { 'banner-item_no-colored': !bannerColored },
    ]"
    :style="{
      'background': bannerBackground,
    }"
  >
    <div
      v-if="$slots.actions"
      class="banner-item__actions d-flex bg-white p-1 rounded"
    >
      <slot name="actions" />
    </div>
    <div class="banner-item__info">
      <div class="flex flex-col">
        <div class="banner-item__title">
          {{ banner.title }}
        </div>
        <div class="banner-item__text mt-2">
          {{ banner.text }}
        </div>
      </div>
      <button
        v-if="banner.buttonText"
        :style="{
          'background': buttonBgColor,
          'color': buttonTextColor,
        }"
        class="banner-item__btn border-0"
      >
        <span class="font-medium"> {{ banner.buttonText }}</span>
      </button>
    </div>
    <div
      class="banner-item__img-block"
      :class="{ 'lg:w-[144px]': !banner.small }"
    >
      <img
        :alt="banner.fileCloudKey || 'banner-img'"
        class="banner-item__img"
        loading="lazy"
        :src="propImageUrl || imageUrl"
        width="144"
        height="200"
      >
      <svg
        v-if="!bannerColored"
        :style="{
          'fill': circleBackground,
        }"
        width="86"
        height="100"
        viewBox="0 0 86 100"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        class="banner-item__circle"
      >
        <circle
          cx="54.5"
          cy="54.5"
          r="54.5"
        />
      </svg>
    </div>
  </div>
</template>

<script>
import { BANNER_STYLE, BANNER_CATEGORY } from '@/helpers/consts';
import { api } from '@/helpers/api';

export default {
  name: 'BannerItem',
  props: {
    banner: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    propImageUrl: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    imageUrl: null,
  }),
  computed: {
    buttonBgColor() {
      switch (this.banner.style) {
        case BANNER_STYLE.PURPLE: {
          return '#FF2069';
        }
        case BANNER_STYLE.ORANGE: {
          return '#FFD200';
        }
        case BANNER_STYLE.BLUE: {
          return '#25DA9D';
        }
        case BANNER_STYLE.GREEN: {
          return '#25DA9D';
        }
        case BANNER_STYLE.PINK: {
          return '#FFD200';
        }
        case BANNER_STYLE.AQUAMARINE: {
          return '#277EFF';
        }
        default:
          return undefined;
      }
    },
    buttonTextColor() {
      switch (this.banner.style) {
        case BANNER_STYLE.PURPLE: {
          return '#fff';
        }
        case BANNER_STYLE.ORANGE: {
          return '#000';
        }
        case BANNER_STYLE.BLUE: {
          return '#000';
        }
        case BANNER_STYLE.GREEN: {
          return '#fff';
        }
        case BANNER_STYLE.PINK: {
          return '#000';
        }
        case BANNER_STYLE.AQUAMARINE: {
          return '#fff';
        }
        default:
          return undefined;
      }
    },
    circleBackground() {
      switch (this.banner.style) {
        case BANNER_STYLE.PURPLE: {
          return '#ffe8f0';
        }
        case BANNER_STYLE.ORANGE: {
          return '#fff0de';
        }
        case BANNER_STYLE.BLUE: {
          return '#dfecff';
        }
        case BANNER_STYLE.GREEN: {
          return '#e2f9de';
        }
        case BANNER_STYLE.PINK: {
          return '#ebfbe9';
        }
        case BANNER_STYLE.AQUAMARINE: {
          return '#e9f9e8';
        }
        default:
          return undefined;
      }
    },
    bannerBackground() {
      if (!this.bannerColored) return undefined;

      switch (this.banner.style) {
        case BANNER_STYLE.PURPLE: {
          return 'linear-gradient(to left, #DE1E75, #6E2A86';
        }
        case BANNER_STYLE.ORANGE: {
          return 'linear-gradient(to left, #FFC100, #E22A08';
        }
        case BANNER_STYLE.BLUE: {
          return 'linear-gradient(to left, #0FD0D9, #006CCD';
        }
        case BANNER_STYLE.GREEN: {
          return 'linear-gradient(to left, #33E478, #149887';
        }
        case BANNER_STYLE.PINK: {
          return 'linear-gradient(to left, #FA5C57, #EF3A7B';
        }
        case BANNER_STYLE.AQUAMARINE: {
          return 'linear-gradient(to left, #29D6AC, #1E9BB7';
        }
        default:
          return undefined;
      }
    },
    bannerColored() {
      return BANNER_CATEGORY.find((item) => item.id === this.banner.category)?.colored;
    },
    bannerSmall() {
      return !this.banner.buttonText;
    },
  },

  async created() {
    if (!this.propImageUrl) {
      const { data } = await api.get('v2/banner/image', {
        params: {
          key: this.banner.fileCloudKey,
        },
        responseType: 'blob',
      });

      this.imageUrl = URL.createObjectURL(data);
    }
  },
};
</script>

<style lang="scss" scoped>
/*! normalize.css v8.0.1 | MIT License | github.com/necolas/normalize.css */
::v-deep p {
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
}

::v-deep ol {
  margin: 0 !important;
  padding: 0 0 0 20px !important;
}

::v-deep ul {
  margin: 0 !important;
  padding: 0 0 0 20px !important;
}

* {
  font-family: 'Inter';
}

.banner-item {
  position: relative;
  display: flex;
  border-radius: 0.75rem;
  height: 200px;
  width: 352px !important;
  background: #fff;
  overflow: hidden;

  &_small {
    height: 112px;
    width: 256px !important;
  }

  &__info {
    padding: 1rem;
    padding-right: 0;
    width: 223px;
  }

  &__btn {
    border-radius: 99999px;
    font-size: 0.875rem;
    line-height: 1.25rem;
    padding-left: 1rem;
    padding-right: 1rem;
    height: 2rem;
    font-weight: 500;
    margin-top: 0.75rem;
  }

  &__img-block {
    position: absolute;
    display: flex;
    right: 0;
    top: 0;
    height: 100%;
    width: 144px;
  }

  &__img {
    object-fit: cover;
    height: 100%;
    z-index: 2;
  }

  &__circle {
    position: absolute;
    right: 0;
    top: 12px;
    z-index: 0;
  }

  &__title {
    font-weight: 500;
    font-size: 1.5rem;
    color: #fff;
    line-height: 1.75rem;
    white-space: pre-line;
  }

  &__text {
    font-size: 1rem;
    color: rgb(243, 244, 245);
    line-height: 1.25rem;
    font-weight: 500;
    white-space: pre-line;
  }

  &__actions {
    position: absolute;
    right: 0;
    top: 0;
    z-index: 5;
    opacity: 0;
    transition: .3s;
  }
}

.banner-item_no-colored .banner-item {

  &__title {
    color: rgba(22, 30, 47, 1);
  }

  &__text {
    color: rgba(127, 131, 141, 1);
  }
}

.banner-item_small .banner-item {

  &__info {
    padding: 0.75rem;
    width: 190px;
    padding-right: 0;
  }

  &__img-block {
    width: 100px;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1;
  }

  &__text {
    font-size: 0.875rem;
    line-height: 1;
    margin-top: 0.5rem;
  }
}

.banner-item:hover .banner-item {
  &__actions {
    opacity: 1;
  }
}
</style>
